import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { JobworkProps } from "../../types/jobworks";
import { ItemsProps } from "../../types/item";
import { JobworksPartiesProps } from "../../types/jobworksParties";
import { PartiesProps } from "../../types/parties";

export interface JobworksState {
  jobworks: JobworkProps[];
  totalJobworks: number;
  totalAmount: number;
  isJobworkModalOpen: boolean;
  editJobwork: JobworkProps | null;
  items: ItemsProps[];
  parties: JobworksPartiesProps[];
  orderParties: PartiesProps[];
  loading: boolean;
  error: string | null;
}

const initialState: JobworksState = {
  jobworks: [],
  totalJobworks: 0,
  totalAmount: 0,
  isJobworkModalOpen: false,
  editJobwork: null,
  items: [],
  parties: [],
  orderParties: [],
  loading: false,
  error: null,
};

type FetchJobworksListDataProps = {
  page: number;
  size?: number;
  fromDate?: string | null;
  toDate?: string | null;
  search?: string | null;
  party_id?: number | null;
  item_id?: number | null;
  status?: number | null;
  type?: number | null;
};

export const fetchJobworksListData = createAsyncThunk(
  "fetchJobworksListData",
  async (req: FetchJobworksListDataProps) => {
    let response: any;
    await GET(`/v1/admin/jobworks`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchItemsList = createAsyncThunk(
  "fetchItemsListForJobworkFilter",
  async () => {
    let response: any;
    await GET(`/v1/admin/items`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchPartiesList = createAsyncThunk(
  "fetchPartiesListForJobworkFilter",
  async () => {
    let response: any;
    await GET(`/v1/admin/jobworks-parties`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchOrderPartiesList = createAsyncThunk(
  "fetchOrderPartiesListForJobworkFilter",
  async () => {
    let response: any;
    await GET(`/v1/admin/parties`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const jobworksSlice = createSlice({
  name: "jobworks",
  initialState,
  reducers: {
    toggleJobworkModal: (state, action: PayloadAction<boolean>) => {
      state.isJobworkModalOpen = action.payload;
    },
    setEditJobwork: (state, action: PayloadAction<JobworkProps | null>) => {
      state.editJobwork = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchJobworksListData.pending, (state, action: any) => {
      state.jobworks = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchJobworksListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload.data.status === "success") {
        state.jobworks = action.payload.data.data.records;
        state.totalJobworks = action.payload.data.data.total_records;
        state.totalAmount = action.payload.data.data.total_amount;
        state.error = null;
      } else {
        state.jobworks = [];
        state.totalAmount = 0;
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchJobworksListData.rejected, (state, action: any) => {
      state.jobworks = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
    builder.addCase(fetchItemsList.pending, (state, action: any) => {
      state.items = [];
    });
    builder.addCase(fetchItemsList.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.items = action.payload.data.data.records;
      } else {
        state.items = [];
      }
    });
    builder.addCase(fetchItemsList.rejected, (state, action: any) => {
      state.items = [];
    });
    builder.addCase(fetchPartiesList.pending, (state, action: any) => {
      state.parties = [];
    });
    builder.addCase(fetchPartiesList.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.parties = action.payload.data.data.records;
      } else {
        state.parties = [];
      }
    });
    builder.addCase(fetchPartiesList.rejected, (state, action: any) => {
      state.parties = [];
    });
    builder.addCase(fetchOrderPartiesList.pending, (state, action: any) => {
      state.orderParties = [];
    });
    builder.addCase(fetchOrderPartiesList.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.orderParties = action.payload.data.data.records;
      } else {
        state.orderParties = [];
      }
    });
    builder.addCase(fetchOrderPartiesList.rejected, (state, action: any) => {
      state.orderParties = [];
    });
  },
});

export const { toggleJobworkModal, setEditJobwork } = jobworksSlice.actions;

export const getJobworks = (state: RootState) => state.jobworks.jobworks;

export const getTotalJobworks = (state: RootState) =>
  state.jobworks.totalJobworks;

export const getTotalAmount = (state: RootState) => state.jobworks.totalAmount;

export const getIsJobworkModalOpen = (state: RootState) =>
  state.jobworks.isJobworkModalOpen;

export const getEditJobwork = (state: RootState) => state.jobworks.editJobwork;

export const getItems = (state: RootState) => state.jobworks.items;

export const getParties = (state: RootState) => state.jobworks.parties;

export const getOrderParties = (state: RootState) =>
  state.jobworks.orderParties;

export default jobworksSlice.reducer;
